import isEqual from 'lodash/isEqual';
import credentialTypes from 'credentialTypes';
import DeleteEntityV2, {DeletionSummary} from 'Mutations/DeleteEntityV2';
import EntityDeletionPreview from 'Queries/EntityDeletionPreview';
import PersonDeletionDetails, {DeletionDetail} from './details';

/** Person deletion requires its own permission; person.all does not imply deletion. */
export const PERSON_DELETE_PERMISSION = 'flytsuite.master.person.delete';

/** Explicit deletion mutation, separate from the read-only preview query. */
export const DELETE_PERSON = DeleteEntityV2;

/**
 * Determines whether the UI may expose the person-delete action.
 *
 * @param groups - Current user's Cognito group names.
 * @returns Whether the explicit delete permission is present, without wildcard expansion.
 * @remarks This is a presentation guard; AppSync and the Java handler enforce authorization.
 */
export function canDeletePerson(groups: string[]): boolean {
    return groups.includes(PERSON_DELETE_PERMISSION);
}

/** A person-oriented presentation of the explicit deletion side effects. */
export type PersonDeletionImpact = {
    personID?: string;
    personName: string;
    credentials: Array<{id: string; label: string; expiration?: string}>;
    pobID?: string;
    pobNavigation?: {transporterID?: string; pobID: string; locationID: string; locationName?: string; transitID?: string; search: string};
    lists?: Array<{id: string; kind: 'whitelist' | 'nonpreferred'; label: string}>;
    referenceUpdates?: Array<{id: string; kind: 'rig' | 'quarters' | 'pob'; label: string}>;
    blockingMessage?: string;
    supportRequired?: boolean;
    blockers?: Array<{id: string; classType: string; reason: string}>;
    passengers: Array<{
        id: string;
        live: boolean;
        retired?: boolean;
        externalOrganization?: boolean;
        customerName?: string;
        transporterName?: string;
        employerName?: string;
        scheduledFlightDate?: string;
        flightAssigned: boolean;
        blocker?: boolean;
        currentFlight?: {
            id: string;
            desig?: string;
            state?: string;
            scheduledFlightDate?: string;
            customerID?: string;
            customerName?: string;
            transporterName?: string;
            transporterID?: string;
        };
    }>;
};

/** Compares displayed side effects and blockers independently of database result ordering. */
export function sameDeletionImpact(previous: PersonDeletionImpact, latest: PersonDeletionImpact): boolean {
    const ordered = <T extends {id: string}>(items: T[] = []) =>
        [...items].sort((left, right) => left.id.localeCompare(right.id));
    const normalize = (preview: PersonDeletionImpact) => ({
        ...preview,
        credentials: ordered(preview.credentials),
        passengers: ordered(preview.passengers),
        lists: ordered(preview.lists),
        referenceUpdates: ordered(preview.referenceUpdates),
        blockers: ordered(preview.blockers)
    });

    return isEqual(normalize(previous), normalize(latest));
}

/** Uses confirmed server results, including updates to records that remain active. */
export function personDeletionSuccess(changes: DeletionSummary[]): string {
    const countTypes = (action: 'DELETE' | 'UPDATE', types: string[]) =>
        changes.filter(change => change.action === action && types.includes(change.classType)).length;
    const count = (amount: number, singular: string) => `${amount} ${singular}${amount === 1 ? '' : 's'}`;
    const deletions = [
        [countTypes('DELETE', ['flytsuite.person']), 'person'],
        [countTypes('DELETE', ['flytsuite.paxnode']), 'passenger'],
        [countTypes('DELETE', ['flytsuite.credential', 'flytsuite.requisite']), 'credential'],
        [countTypes('DELETE', ['flytsuite.pobnode']), 'POB record']
    ] as Array<[number, string]>;
    const memberships = [
        [countTypes('UPDATE', ['flytsuite.whitelist']), 'whitelist'],
        [countTypes('UPDATE', ['flytsuite.nonpreferred']), 'non-preferred list']
    ] as Array<[number, string]>;
    const references = [
        [countTypes('UPDATE', ['flytsuite.rig']), 'rig'],
        [countTypes('UPDATE', ['flytsuite.quartersnode']), 'quarters record'],
        [countTypes('UPDATE', ['flytsuite.pobnode']), 'other POB record']
    ] as Array<[number, string]>;
    const describe = (items: Array<[number, string]>) => items.filter(([amount]) => amount)
        .map(([amount, label]) => count(amount, label)).join(', ');
    const sentences = [`Deleted ${describe(deletions)}`];
    if (describe(memberships)) sentences.push(`removed this person from ${describe(memberships)}`);
    if (describe(references)) sentences.push(`cleared references in ${describe(references)}`);

    return `${sentences.join('; ')}.`;
}

/** Describes only the loaded preview's changes for the required acknowledgement. */
export function deletionAcknowledgement(preview: PersonDeletionImpact): string {
    const count = (amount: number, singular: string, plural = `${singular}s`) =>
        `${amount} ${amount === 1 ? singular : plural}`;
    const deleted = ['this person'];
    if (preview.passengers.length) deleted.push(count(preview.passengers.length, 'passenger'));
    if (preview.credentials.length) deleted.push(count(preview.credentials.length, 'credential'));
    if (preview.pobID) deleted.push('1 POB record');

    const removed = [];
    const whitelists = (preview.lists || []).filter(list => list.kind === 'whitelist').length;
    const nonPreferred = (preview.lists || []).filter(list => list.kind === 'nonpreferred').length;
    if (whitelists) removed.push(count(whitelists, 'whitelist'));
    if (nonPreferred) removed.push(count(nonPreferred, 'non-preferred list'));
    const memberships = removed.length ? ` and remove this person from ${removed.join(' and ')}` : '';
    const updates = preview.referenceUpdates || [];
    const references = [
        [updates.filter(item => item.kind === 'rig').length, 'rig'],
        [updates.filter(item => item.kind === 'quarters').length, 'quarters record'],
        [updates.filter(item => item.kind === 'pob').length, 'other POB record']
    ] as Array<[number, string]>;
    const modified = references.filter(([amount]) => amount).map(([amount, label]) => count(amount, label));
    const cleared = modified.length ? ` and clear references in ${modified.join(', ')}` : '';
    return `I acknowledge that this will delete ${deleted.join(', ')}${memberships}${cleared}.`;
}

/** Checks local inputs before either operation; the server independently enforces permission. */
function requirePersonDeletionAccess(groups: string[], personID: string, orgID: string) {
    if (!canDeletePerson(groups)) throw new Error('You do not have permission to delete persons.');
    if (!personID || !orgID) throw new Error('Select an existing person and organization before deleting.');
}

/** Membership lists are updated rather than soft-deleted alongside the person. */
const membershipListTypes = ['flytsuite.whitelist', 'flytsuite.nonpreferred'];

/** Side effects carry an explicit action; only DELETE results require a deletion timestamp. */
function requireSummary(entities: DeletionSummary[], personID: string, deleted: boolean): DeletionSummary[] {
    if (!Array.isArray(entities) || !entities.some(entity => entity._id === personID && entity.classType === 'flytsuite.person' && entity.action === 'DELETE')
        || entities.some(entity => (entity.action !== 'DELETE' && entity.action !== 'UPDATE') || typeof entity.blocker !== 'boolean')
        || (deleted && entities.some(entity => entity.action === 'DELETE' && !entity.deletedTs))) {
        throw new Error('The server did not confirm the affected records. Refresh before trying again.');
    }
    return entities;
}

/** Preserves the server's complete blocked side-effect list for the confirmation UI. */
export class PersonDeletionBlockedError extends Error {
    constructor(public readonly changes: DeletionSummary[]) {
        super(changes.filter(change => change.blocker)
            .map(change => change.blockingReason || `Deletion is blocked by ${change._id}.`).join(' '));
        this.name = 'PersonDeletionBlockedError';
    }
}

/** Only confirmation calls this mutation. The server reloads and validates records before writing. */
export async function deletePerson(client: any, groups: string[], personID: string, orgID: string, revision?: string) {
    requirePersonDeletionAccess(groups, personID, orgID);
    const result = await client.mutate({mutation: DELETE_PERSON, variables: {_id: personID, tpID: orgID, ...(revision !== undefined ? {_rev: revision} : {})},
        fetchPolicy: 'no-cache', errorPolicy: 'all'});
    const changes = result.data?.deleteEntityV2 || result.errors?.find(error => Array.isArray(error.data))?.data;
    if (Array.isArray(changes) && changes.some(change => change.blocker)) {
        throw new PersonDeletionBlockedError(requireSummary(changes, personID, false));
    }
    if (result.errors?.length) throw new Error(result.errors.map(error => error.message).join('; '));
    return requireSummary(changes, personID, true);
}

/** Masks names only for other tenants of the signed-in account's organization type. */
function externalOrganization(organizationID: string | undefined, accountOrganizationID?: string): boolean {
    return !!organizationID && !!accountOrganizationID
        && /^(CUS|EMP|TRN)-/.test(accountOrganizationID)
        && organizationID.split('-')[0] === accountOrganizationID.split('-')[0]
        && organizationID.toUpperCase() !== accountOrganizationID.toUpperCase();
}

function organizationLabel(organization: {_id: string; name?: string | null} | null | undefined, accountOrganizationID?: string): string | undefined {
    return externalOrganization(organization?._id, accountOrganizationID)
        ? 'External organization' : organization?.name || organization?._id;
}

/** Loads the read-only preview, then looks up its selected document IDs for display and flight blockers. */
export async function loadDeletionImpact(
    client: any, groups: string[], personID: string, orgID: string, accountOrganizationID = orgID
): Promise<PersonDeletionImpact> {
    requirePersonDeletionAccess(groups, personID, orgID);
    const result = await client.query({query: EntityDeletionPreview, variables: {_id: personID, tpID: orgID},
        fetchPolicy: 'no-cache', errorPolicy: 'none'}).catch(error => {
        if (error.message?.includes('FieldUndefined') && error.message?.includes('getEntityDeletionPreview')) {
            throw new Error('Deletion preview is unavailable. Contact your administrator.');
        }
        throw error;
    });
    if (result.errors?.length) throw new Error(result.errors.map(error => error.message).join('; '));
    return describePersonDeletion(client, result.data?.getEntityDeletionPreview, personID, accountOrganizationID);
}

/** Resolves display details for authoritative server side effects, including a blocked DELETE response. */
export async function describePersonDeletion(client: any, changes: DeletionSummary[], personID: string, accountOrganizationID?: string): Promise<PersonDeletionImpact> {
    const summaries = requireSummary(changes, personID, false);
    const retainedTypes = [...membershipListTypes, 'flytsuite.rig', 'flytsuite.quartersnode'];
    if (summaries.some(summary => summary.classType === 'flytsuite.pobnode' ? false
        : (summary.action === 'UPDATE') !== retainedTypes.includes(summary.classType))) {
        throw new Error('Unable to display all deletion side effects. Refresh before trying again.');
    }
    const idsFor = (...types: string[]) => summaries.filter(summary => types.includes(summary.classType)).map(summary => summary._id);
    const listIDs = summaries.filter(summary => membershipListTypes.includes(summary.classType)).map(summary => summary._id);
    // List IDs use WHL-/NPF- in place of their owning customer's CUS- prefix.
    const listCustomerIDs = Array.from(new Set(listIDs.map(id => id.replace(/^(WHL|NPF)-/, 'CUS-'))));
    const details = await client.query({query: PersonDeletionDetails,
        variables: {personIDs: idsFor('flytsuite.person'), credentialIDs: idsFor('flytsuite.credential', 'flytsuite.requisite'),
            passengerIDs: idsFor('flytsuite.paxnode'), pobIDs: idsFor('flytsuite.pobnode'),
            whiteListIDs: idsFor('flytsuite.whitelist'), nonPreferredIDs: idsFor('flytsuite.nonpreferred'), listCustomerIDs,
            rigIDs: idsFor('flytsuite.rig'), quartersIDs: idsFor('flytsuite.quartersnode')},
        fetchPolicy: 'no-cache', errorPolicy: 'none'});
    if (details.errors?.length) throw new Error(details.errors.map(error => error.message).join('; '));
    const entities: DeletionDetail[] = ['persons', 'credentials', 'passengers', 'pob', 'whitelists', 'nonPreferredLists', 'rigs', 'quarters']
        .flatMap(key => details.data?.[key] || []);
    const expectedTypes = {'flytsuite.person': 'Person', 'flytsuite.credential': 'Credential',
        'flytsuite.requisite': 'Credential', 'flytsuite.paxnode': 'PaxNode', 'flytsuite.pobnode': 'PobNode',
        'flytsuite.whitelist': 'WhiteList', 'flytsuite.nonpreferred': 'NonPreferred',
        'flytsuite.rig': 'Rig', 'flytsuite.quartersnode': 'QuartersNode'};
    if (entities.length !== summaries.length || new Set(entities.map(entity => entity?._id)).size !== summaries.length
        || summaries.some(summary => !entities.some(entity => entity?._id === summary._id
            && entity.classType === summary.classType && entity.__typename === expectedTypes[summary.classType]
            && (!entity['deletedTs'] || (summary.action === 'DELETE' && !!summary.deletedTs))))) {
        throw new Error('Unable to load all affected records. Refresh before trying again.');
    }
    const person = entities.find((entity): entity is Extract<DeletionDetail, {__typename?: 'Person'}> =>
        entity.__typename === 'Person' && entity._id === personID)!;
    const credentials = entities.filter((entity): entity is Extract<DeletionDetail, {__typename?: 'Credential'}> =>
        entity.__typename === 'Credential');
    const passengers = entities.filter((entity): entity is Extract<DeletionDetail, {__typename?: 'PaxNode'}> =>
        entity.__typename === 'PaxNode');
    const pob = entities.find((entity): entity is Extract<DeletionDetail, {__typename?: 'PobNode'}> =>
        entity.__typename === 'PobNode' && summaries.some(summary => summary._id === entity._id && summary.action === 'DELETE'));
    const pobOwnerID = accountOrganizationID?.startsWith('CUS-') ? pob?.customerID?._id
        : accountOrganizationID?.startsWith('EMP-') ? pob?.employerID?._id
        : accountOrganizationID?.startsWith('TRN-') ? pob?.pobTransporter?._id : undefined;
    const canOpenPob = !!pobOwnerID && pobOwnerID.toUpperCase() === accountOrganizationID?.toUpperCase();
    const blockers = summaries.filter(summary => summary.blocker).map(summary => ({id: summary._id,
        classType: summary.classType, reason: summary.blockingReason || `Deletion is blocked by ${summary._id}.`}));
    const supportRequired = summaries.some(summary => summary.blocker
        && ['flytsuite.pobnode', 'flytsuite.rig', 'flytsuite.quartersnode'].includes(summary.classType))
        || passengers.some(passenger => {
            if (!summaries.some(summary => summary._id === passenger._id && summary.blocker)) return false;
            if (accountOrganizationID?.startsWith('CUS-')) {
                return !passenger.customerID?._id || externalOrganization(passenger.customerID._id, accountOrganizationID);
            }
            if (accountOrganizationID?.startsWith('EMP-')) {
                return !passenger.employerID?._id || externalOrganization(passenger.employerID._id, accountOrganizationID);
            }
            if (accountOrganizationID?.startsWith('TRN-')) {
                return !passenger.tpID?._id || externalOrganization(passenger.tpID._id, accountOrganizationID);
            }
            return false;
        });
    return {
        supportRequired,
        personID: person._id,
        personName: `${person.lastName}, ${person.firstName}`,
        credentials: credentials.map(credential => ({id: credential._id,
            label: credentialTypes[credential.type]?.label || 'Credential', expiration: credential.expiration})),
        pobID: pob?._id,
        pobNavigation: canOpenPob && pob?.lastKnownController?._id ? {
            pobID: pob._id,
            transporterID: pob.pobTransporter?._id,
            locationID: pob.lastKnownController._id,
            locationName: pob.lastKnownController.name,
            transitID: pob.currentCarrierID && '_id' in pob.currentCarrierID ? pob.currentCarrierID._id : undefined,
            search: `${pob.firstName || person.firstName || ''} ${pob.lastName || person.lastName || ''}`.trim()
        } : undefined,
        lists: summaries.filter(summary => membershipListTypes.includes(summary.classType)).map(summary => {
            const customerID = summary._id.replace(/^(WHL|NPF)-/, 'CUS-');
            const customer = details.data?.listCustomers?.find(customer => customer?._id === customerID);
            return {id: summary._id, kind: summary.classType === 'flytsuite.whitelist' ? 'whitelist' : 'nonpreferred',
                label: externalOrganization(customerID, accountOrganizationID) ? 'External organization'
                    : customer?.__typename === 'Customer' && customer.name ? customer.name : summary._id};
        }),
        referenceUpdates: summaries.filter(summary => summary.action === 'UPDATE'
            && !membershipListTypes.includes(summary.classType)).map(summary => {
            const entity = entities.find(entity => entity._id === summary._id)!;
            if (entity.__typename === 'Rig') {
                return {id: entity._id, kind: 'rig' as const, label: summary.blocker ? 'Restricted record' : entity.name || entity._id};
            }
            if (entity.__typename === 'QuartersNode') {
                return {id: entity._id, kind: 'quarters' as const,
                    label: summary.blocker ? 'Restricted record' : entity.name ? `${entity.name} (${entity.quartersType || 'Quarters'})` : entity._id};
            }
            const other = entity as Extract<DeletionDetail, {__typename?: 'PobNode'}>;
            return {id: other._id, kind: 'pob' as const,
                label: summary.blocker ? 'Restricted record' : other.firstName || other.lastName ? `${other.lastName || ''}, ${other.firstName || ''}` : other._id};
        }),
        blockers,
        blockingMessage: blockers.length ? blockers.map(blocker => blocker.reason).join(' ') : undefined,
        passengers: passengers.map(passenger => {
            const external = [passenger.customerID, passenger.tpID, passenger.employerID]
                .some(organization => externalOrganization(organization?._id, accountOrganizationID));
            return {
                id: passenger._id,
                externalOrganization: external,
                live: passenger.nodeState !== 'RETIRED' && passenger.checkInType !== 'SCHEDULED',
                retired: passenger.nodeState === 'RETIRED',
                customerName: external ? 'External organization' : organizationLabel(passenger.customerID, accountOrganizationID),
                transporterName: external ? 'External organization' : organizationLabel(passenger.tpID, accountOrganizationID),
                employerName: external ? 'External organization' : organizationLabel(passenger.employerID, accountOrganizationID),
                scheduledFlightDate: passenger.scheduledFlightDate,
                flightAssigned: !!passenger.currentCarrierKey?.trim(),
                blocker: summaries.find(summary => summary._id === passenger._id)?.blocker === true,
                currentFlight: external ? undefined : passenger.currentCarrierID ? {id: passenger.currentCarrierID._id,
                    desig: passenger.currentCarrierID.desig,
                    state: passenger.currentCarrierID.state,
                    customerID: passenger.currentCarrierID.customerID?._id,
                    scheduledFlightDate: passenger.currentCarrierID.scheduledFlightDate,
                    customerName: organizationLabel(passenger.currentCarrierID.customerID, accountOrganizationID),
                    transporterID: passenger.currentCarrierID.tpID?._id,
                    transporterName: organizationLabel(passenger.currentCarrierID.tpID, accountOrganizationID)}
                    : passenger.currentCarrierKey?.trim() ? {id: passenger.currentCarrierKey} : undefined
            };
        })
    };
}
